import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Star, Building2, Package, User, Calendar, Trash2, Loader2, Download } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../store/useAppStore";
import { sessionsService } from "../services/sessionsService";
import { companiesService } from "../services/companiesService";
import { productsService } from "../services/productsService";
import type { Company, EventSession, Product } from "../types";
import { PageShell } from "../components/PageShell";
import { PageHeader } from "../components/PageHeader";
import { GlassCard } from "../components/GlassCard";
import { CompanyAvatar } from "../components/CompanyAvatar";
import { EmptyState } from "../components/EmptyState";
import { toast } from "sonner";
import { formatCompanyLocation } from "../utils/locationLabel";
import { markLabel } from "../utils/contactMarks";
import { downloadCsv } from "../utils/downloadCsv";
import { contactsService } from "../services/contactsService";
import { formatAnnotationNotesPreview } from "../utils/annotationNotes";

type FavoriteCategory = "all" | "companies" | "products" | "representatives" | "sessions";

type FavoriteItem =
  | {
      type: "company";
      data: Company;
      savedAt: string;
      notes?: string;
      tags?: string[];
    }
  | { type: "product"; data: Product; savedAt: string }
  | { type: "session"; data: EventSession; savedAt: string };

export function FavoritesPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, selectedEventId, savedContacts, removeSavedContact } = useAppStore();
  const [category, setCategory] = useState<FavoriteCategory>("all");
  const [allFavorites, setAllFavorites] = useState<FavoriteItem[]>([]);
  const [loading, setLoading] = useState(true);

  const loadFavorites = useCallback(async () => {
    if (!selectedEventId) {
      setAllFavorites([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const contactItems = await Promise.all(
        savedContacts
          .filter((c) => c.eventId === selectedEventId)
          .map(async (c) => {
            const company = await companiesService.getById(c.companyId, selectedEventId);
            return company
              ? ({
                  type: "company" as const,
                  data: company,
                  savedAt: c.savedAt,
                  notes: c.notes,
                  tags: c.tags,
                })
              : null;
          }),
      );

      const [sessions, products] = await Promise.all([
        sessionsService.getUserFavorites(user.id, selectedEventId).catch(() => [] as EventSession[]),
        productsService.getFavorites(selectedEventId).catch(() => [] as Product[]),
      ]);

      const sessionItems: FavoriteItem[] = sessions.map((session) => ({
        type: "session",
        data: session,
        savedAt: session.startTime,
      }));

      const productItems: FavoriteItem[] = products.map((product) => ({
        type: "product",
        data: product,
        savedAt: product.favoritedAt ?? new Date().toISOString(),
      }));

      const combined = [...contactItems, ...productItems, ...sessionItems].filter(
        (item): item is FavoriteItem => item !== null,
      );

      setAllFavorites(
        combined.sort(
          (a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime(),
        ),
      );
    } catch {
      setAllFavorites([]);
    } finally {
      setLoading(false);
    }
  }, [i18n.language, savedContacts, selectedEventId, user.id]);

  useEffect(() => {
    void loadFavorites();
  }, [loadFavorites]);

  const filteredFavorites = useMemo(() => {
    if (category === "all") return allFavorites;
    return allFavorites.filter((f) => {
      if (category === "companies") return f.type === "company";
      if (category === "products") return f.type === "product";
      if (category === "sessions") return f.type === "session";
      return false;
    });
  }, [allFavorites, category]);

  const groupedFavorites = useMemo(() => {
    const company: Extract<FavoriteItem, { type: "company" }>[] = [];
    const product: Extract<FavoriteItem, { type: "product" }>[] = [];
    const session: Extract<FavoriteItem, { type: "session" }>[] = [];
    filteredFavorites.forEach((f) => {
      if (f.type === "company") company.push(f);
      else if (f.type === "product") product.push(f);
      else if (f.type === "session") session.push(f);
    });
    return { company, product, session };
  }, [filteredFavorites]);

  const handleRemoveFavorite = async (item: FavoriteItem) => {
    try {
      if (item.type === "company") {
        removeSavedContact(item.data.id, selectedEventId ?? undefined);
        await contactsService.remove(item.data.id, selectedEventId ?? undefined);
        setAllFavorites((prev) =>
          prev.filter((f) => !(f.type === "company" && f.data.id === item.data.id)),
        );
        toast.success(t("favorites.company_removed"), { id: "fav-remove" });
        return;
      }
      if (item.type === "product" && selectedEventId) {
        await productsService.removeFavorite(selectedEventId, item.data.id);
        setAllFavorites((prev) =>
          prev.filter((f) => !(f.type === "product" && f.data.id === item.data.id)),
        );
        toast.success(t("favorites.product_removed"), { id: "fav-remove" });
        return;
      }
      if (item.type === "session" && selectedEventId) {
        await sessionsService.removeFromFavorites(user.id, item.data.id, selectedEventId);
        setAllFavorites((prev) =>
          prev.filter((f) => !(f.type === "session" && f.data.id === item.data.id)),
        );
        toast.success(t("favorites.session_removed"), { id: "fav-remove" });
      }
    } catch {
      toast.error(t("visitor.camera_open_failed"), { id: "fav-remove-err" });
      void loadFavorites();
    }
  };

  const handleNavigate = (item: FavoriteItem) => {
    if (item.type === "company") {
      navigate(`/companies/${item.data.id}`);
    } else if (item.type === "product") {
      navigate(`/products/${item.data.id}`);
    } else if (item.type === "session") {
      navigate(`/schedule/${item.data.id}`);
    }
  };

  const handleExport = () => {
    if (!filteredFavorites.length) {
      toast.error(t("favorites.export_empty"));
      return;
    }
    const rows = filteredFavorites.map((item) => {
      if (item.type === "company") {
        return [
          "company",
          item.data.name || "",
          item.data.booth || "",
          formatCompanyLocation({
            hall: item.data.hall,
            booth: item.data.booth,
          }),
          (item.tags ?? []).map((tag) => markLabel(tag, t)).join(", "),
          formatAnnotationNotesPreview(item.notes, item.tags ?? [], "\n"),
          item.savedAt,
        ];
      }
      if (item.type === "product") {
        return [
          "product",
          item.data.name || "",
          item.data.companyId || "",
          item.data.category || "",
          "",
          "",
          item.savedAt,
        ];
      }
      return [
        "session",
        item.data.title || "",
        item.data.location || "",
        item.data.startTime || "",
        "",
        "",
        item.savedAt,
      ];
    });
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(
      `mvk-favorites-${stamp}.csv`,
      ["type", "title", "extra", "details", "tags", "notes", "saved_at"],
      rows,
    );
    toast.success(t("favorites.export_ok"));
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "short",
    });
  };

  return (
    <>
      <PageHeader
        title={t("favorites.title")}
        subtitle={t("favorites.subtitle", { n: filteredFavorites.length })}
        back
        right={
          filteredFavorites.length > 0 ? (
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/80 border border-gray-200 text-gray-700"
              aria-label={t("favorites.export")}
              title={t("favorites.export")}
            >
              <Download size={18} />
            </button>
          ) : undefined
        }
      />
      <PageShell>
        <div className="mt-3 space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
              {[
                { value: "all" as FavoriteCategory, label: t("favorites.cat_all"), icon: Star },
                { value: "companies" as FavoriteCategory, label: t("favorites.cat_companies"), icon: Building2 },
                { value: "products" as FavoriteCategory, label: t("favorites.cat_products"), icon: Package },
                { value: "representatives" as FavoriteCategory, label: t("favorites.cat_people"), icon: User },
                { value: "sessions" as FavoriteCategory, label: t("favorites.cat_sessions"), icon: Calendar },
              ].map((cat) => {
                const Icon = cat.icon;
                const isActive = category === cat.value;
                const count =
                  cat.value === "all"
                    ? allFavorites.length
                    : cat.value === "companies"
                      ? groupedFavorites.company.length
                      : cat.value === "products"
                        ? groupedFavorites.product.length
                        : cat.value === "sessions"
                          ? groupedFavorites.session.length
                          : 0;
                return (
                  <button
                    key={cat.value}
                    onClick={() => setCategory(cat.value)}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-medium whitespace-nowrap transition-all ${
                      isActive
                        ? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/20"
                        : "bg-white text-gray-600 border border-gray-200 hover:border-indigo-200"
                    }`}
                  >
                    <Icon size={14} />
                    {cat.label}
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[11px] ${
                        isActive ? "bg-white/20" : "bg-gray-100"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>

          {loading && (
            <div className="flex justify-center py-12 text-gray-400">
              <Loader2 size={28} className="animate-spin" />
            </div>
          )}

          {!loading && filteredFavorites.length === 0 ? (
            <EmptyState
              icon={<Star size={32} />}
              title={t("favorites.empty_title")}
              description={
                category === "all"
                  ? t("favorites.empty_all")
                  : t("favorites.empty_category")
              }
            />
          ) : !loading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              className="space-y-5"
            >
              {groupedFavorites.company.length > 0 && (
                <div>
                  <h3 className="text-[14px] text-gray-500 mb-2 px-1 flex items-center gap-2">
                    <Building2 size={14} />
                    {t("search.section_companies", { n: groupedFavorites.company.length })}
                  </h3>
                  <div className="space-y-2">
                    {groupedFavorites.company.map((item, i) => {
                      const notesPreview = formatAnnotationNotesPreview(
                        item.notes,
                        item.tags ?? [],
                      );
                      return (
                      <motion.div
                        key={item.data.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05, duration: 0.3 }}
                      >
                        <GlassCard variant="default" padding="sm">
                          <div className="flex items-center gap-3">
                            <CompanyAvatar name={item.data.name} logoUrl={item.data.logoUrl} size="md" />
                            <div
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => handleNavigate(item)}
                            >
                              <p className="text-[14px] text-gray-900 truncate">
                                {item.data.name}
                              </p>
                              <p className="text-[12px] text-gray-500 truncate">
                                {[item.data.category, formatCompanyLocation(item.data)]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {t("favorites.added", { date: formatDate(item.savedAt) })}
                              </p>
                              {item.tags && item.tags.length > 0 ? (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {item.tags.slice(0, 4).map((tag) => (
                                    <span
                                      key={tag}
                                      className="text-[10px] text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded"
                                    >
                                      {markLabel(tag, t)}
                                    </span>
                                  ))}
                                </div>
                              ) : null}
                              {notesPreview ? (
                                <p className="text-[12px] text-gray-600 mt-1 line-clamp-2">
                                  {notesPreview}
                                </p>
                              ) : null}
                            </div>
                            <button
                              onClick={() => handleRemoveFavorite(item)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                            >
                              <Trash2 size={16} className="text-red-400" />
                            </button>
                          </div>
                        </GlassCard>
                      </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}

              {groupedFavorites.product.length > 0 && (
                <div>
                  <h3 className="text-[14px] text-gray-500 mb-2 px-1 flex items-center gap-2">
                    <Package size={14} />
                    {t("search.section_products", { n: groupedFavorites.product.length })}
                  </h3>
                  <div className="space-y-2">
                    {groupedFavorites.product.map((item, i) => (
                      <motion.div
                        key={item.data.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05, duration: 0.3 }}
                      >
                        <GlassCard variant="default" padding="sm">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {item.data.images[0] ? (
                                <img
                                  src={item.data.images[0]}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <Package size={18} className="text-amber-600" />
                              )}
                            </div>
                            <div
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => handleNavigate(item)}
                            >
                              <p className="text-[14px] text-gray-900 line-clamp-2">
                                {item.data.name}
                              </p>
                              <p className="text-[12px] text-gray-500 truncate">
                                {item.data.category || t("favorites.product")}
                              </p>
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {t("favorites.added", { date: formatDate(item.savedAt) })}
                              </p>
                            </div>
                            <button
                              onClick={() => handleRemoveFavorite(item)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                            >
                              <Trash2 size={16} className="text-red-400" />
                            </button>
                          </div>
                        </GlassCard>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {groupedFavorites.session.length > 0 && (
                <div>
                  <h3 className="text-[14px] text-gray-500 mb-2 px-1 flex items-center gap-2">
                    <Calendar size={14} />
                    {t("search.section_sessions", { n: groupedFavorites.session.length })}
                  </h3>
                  <div className="space-y-2">
                    {groupedFavorites.session.map((item, i) => (
                      <motion.div
                        key={item.data.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05, duration: 0.3 }}
                      >
                        <GlassCard variant="default" padding="sm">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                              <Calendar size={18} className="text-indigo-600" />
                            </div>
                            <div
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => handleNavigate(item)}
                            >
                              <p className="text-[14px] text-gray-900 line-clamp-2">
                                {item.data.title}
                              </p>
                              <p className="text-[12px] text-gray-500 mt-0.5">
                                {new Date(item.data.startTime).toLocaleString(i18n.language, {
                                  day: "numeric",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {t("favorites.added", { date: formatDate(item.savedAt) })}
                              </p>
                            </div>
                            <button
                              onClick={() => handleRemoveFavorite(item)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                            >
                              <Trash2 size={16} className="text-red-400" />
                            </button>
                          </div>
                        </GlassCard>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          ) : null}
        </div>
      </PageShell>
    </>
  );
}
